import React, { Component } from "react";
import { AppCard } from "../../components/ui";
import PageLayout from "../../components/ui/PageLayout";
import history from "../../history";
import { Users as UsersIcon, ChevronDown } from "lucide-react";
import Individuals from "./Individuals";
import Leads from "./Leads";
import { isRestrictedToLeadsView, isSuperAdmin } from "../../authGuard";

export type ViewFilter = "individuals" | "leads";

const VIEW_OPTIONS: { value: ViewFilter; label: string }[] = [
    { value: "individuals", label: "Customers" },
    { value: "leads", label: "Leads" },
];

function getAvailableOptions() {
    const hasAccess = isRestrictedToLeadsView() || isSuperAdmin();
    return hasAccess ? VIEW_OPTIONS : VIEW_OPTIONS.filter((o) => o.value === "leads");
}

/* ------------------------------------------------------------------ */
/*  Standalone dropdown component — can be used by parent (Dashboard) */
/* ------------------------------------------------------------------ */
interface ViewFilterDropdownProps {
    activeView: ViewFilter;
    onChange: (view: ViewFilter) => void;
}

interface ViewFilterDropdownState {
    open: boolean;
}

export class ViewFilterDropdown extends Component<ViewFilterDropdownProps, ViewFilterDropdownState> {
    private ref = React.createRef<HTMLDivElement>();

    state: ViewFilterDropdownState = { open: false };

    componentDidMount() {
        document.addEventListener("mousedown", this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleClickOutside);
    }

    handleClickOutside = (event: MouseEvent) => {
        if (this.ref.current && !this.ref.current.contains(event.target as Node)) {
            this.setState({ open: false });
        }
    };

    render() {
        const options = getAvailableOptions();
        if (options.length <= 1) return null;

        const activeLabel = options.find((o) => o.value === this.props.activeView)?.label ?? "Select";

        return (
            <div className="relative" ref={this.ref}>
                <button
                    type="button"
                    onClick={() => this.setState((prev) => ({ open: !prev.open }))}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                >
                    {activeLabel}
                    <ChevronDown size={14} className={`transition-transform ${this.state.open ? "rotate-180" : ""}`} />
                </button>
                {this.state.open && (
                    <div className="absolute right-0 mt-1 w-40 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-lg z-10 py-1">
                        {options.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                    this.props.onChange(option.value);
                                    this.setState({ open: false });
                                }}
                                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                    this.props.activeView === option.value
                                        ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium"
                                        : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                                }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

/* ------------------------------------------------------------------ */
/*  Users page                                                        */
/* ------------------------------------------------------------------ */
interface UsersProps {
    merchantId: number | null;
    isDashboard: boolean;
    /** When controlled externally (dashboard), parent passes the active view */
    activeView?: ViewFilter;
}

interface State {
    activeView: ViewFilter;
    fromSearch: boolean;
    hasIndividualsData: boolean;
    hasLeadsData: boolean;
}

export default class Users extends Component<UsersProps, State> {
    detectPath: (() => void) | null = null;

    constructor(props: UsersProps) {
        super(props);
        const defaultView = (!isRestrictedToLeadsView() || isSuperAdmin()) ? "individuals" : "leads";
        this.state = {
            activeView: props.activeView ?? defaultView,
            fromSearch: false,
            hasIndividualsData: false,
            hasLeadsData: false,
        };
    }

    async componentDidMount() {
        await this.setTabFromPath(window.location.pathname.toLowerCase());

        this.detectPath = history.listen(async (location) => {
            if ((location.action == "PUSH" || location.action == "REPLACE") && window.location.pathname.toLowerCase()) {
                await this.setTabFromPath(window.location.pathname.toLowerCase());
            }
        });
    }

    componentWillUnmount() {
        if (this.detectPath) {
            this.detectPath();
        }
    }

    componentDidUpdate(prevProps: UsersProps) {
        if (this.props.activeView && this.props.activeView !== prevProps.activeView) {
            this.setState({ activeView: this.props.activeView });
        }
    }

    isIndividualsAllowedAccess = () => {
        return !isRestrictedToLeadsView() || isSuperAdmin();
    };

    setTabFromPath(path: string) {
        if (path == "/users") {
            const queryParams = new URLSearchParams(history.location.search);
            const isFromSearch = queryParams.get("fromSearch");
            const userData = queryParams.get("userData");

            if (isFromSearch == "true" && userData) {
                const users = JSON.parse(userData);
                const hasIndividuals = users.some((user: { id: number }) => user.id !== 0);
                const hasLeads = users.some((user: { id: number }) => user.id === 0);

                this.setState({
                    fromSearch: true,
                    hasIndividualsData: this.isIndividualsAllowedAccess() ? hasIndividuals : false,
                    hasLeadsData: hasLeads,
                });
            } else {
                this.setState({
                    fromSearch: false,
                    hasIndividualsData: false,
                    hasLeadsData: false,
                });
            }
        }
    }

    /** The current effective view (prop-controlled or internal state) */
    get currentView(): ViewFilter {
        return this.props.activeView ?? this.state.activeView;
    }

    renderActiveTable = () => {
        if (this.currentView === "individuals" && this.isIndividualsAllowedAccess()) {
            return <Individuals onClear={() => this.setState({ fromSearch: false })} merchantId={this.props.merchantId} isDashboard={this.props.isDashboard} />;
        }
        return <Leads onClear={() => this.setState({ fromSearch: false })} merchantId={this.props.merchantId} isDashboard={this.props.isDashboard} />;
    };

    render() {
        // Search results mode — show both sections in separate cards
        if (this.state.fromSearch) {
            const searchContent = (
                <>
                    {this.state.hasIndividualsData && (
                        <AppCard className="mb-3">
                            <AppCard.Body>
                                <h3 className="text-xl font-semibold mb-3 text-gray-700">Customers</h3>
                                <Individuals onClear={() => this.setState({ fromSearch: false })} merchantId={this.props.merchantId} isDashboard={this.props.isDashboard} />
                            </AppCard.Body>
                        </AppCard>
                    )}
                    {this.state.hasLeadsData && (
                        <AppCard className="mb-3">
                            <AppCard.Body>
                                <h3 className="text-xl font-semibold mb-3 text-gray-700">Leads</h3>
                                <Leads onClear={() => this.setState({ fromSearch: false })} merchantId={this.props.merchantId} isDashboard={this.props.isDashboard} />
                            </AppCard.Body>
                        </AppCard>
                    )}
                </>
            );

            if (this.props.isDashboard) return searchContent;
            return (
                <PageLayout heading="Customers" icon={UsersIcon}>
                    {searchContent}
                </PageLayout>
            );
        }

        // Dashboard mode — parent handles card wrapper, header, and dropdown
        if (this.props.isDashboard) {
            return this.renderActiveTable();
        }

        // Standalone page mode — self-contained with own dropdown
        return (
            <PageLayout
                heading="Customers"
                icon={UsersIcon}
                actions={
                    <ViewFilterDropdown
                        activeView={this.currentView}
                        onChange={(view) => this.setState({ activeView: view })}
                    />
                }
            >
                <AppCard>
                    <AppCard.Body className="!p-0">
                        {this.renderActiveTable()}
                    </AppCard.Body>
                </AppCard>
            </PageLayout>
        );
    }
}
